import { ReactNode, createContext, useContext } from 'react'
import {
    createCopilotPreset,
    deleteCopilotPreset,
    fetchCopilotPresetsById,
    updateCopilotPreset,
} from 'shared/api/creation'
import UserContext from 'shared/contexts/user'
import { CopilotUploadPreset } from 'shared/types/copilot'
import { PortalMessageContext } from 'ui'
import { MAX_TAG_LIMIT } from '../../../../constants'
import { CreationToolUploadContext } from '../../manager'
import {
    PresetCreatedMessage,
    PresetDeletedMessage,
    PresetDuplicatedMessage,
    PresetImportedMessage,
    PresetRenamedMessage,
    PresetUpdatedMessage,
} from '../messages'
import { createDataFromPreset } from '../utils/preset'
import { createDataFromUploadContext } from '../utils/upload'

export type CopilotApiContextProps = {
    createNewPreset: (title: string) => void
    deletePreset: (preset: CopilotUploadPreset) => void
    duplicatePreset: (preset: CopilotUploadPreset, title?: string) => void
    importPresetsById: (ids: string[], ignoreMessage?: boolean) => void
    renamePreset: (preset: CopilotUploadPreset, newName: string) => void
    updatePreset: (preset: CopilotUploadPreset) => void
}

export const CopilotApiContext = createContext({} as CopilotApiContextProps)

type Props = {
    children: ReactNode
    onCreate?: (preset: CopilotUploadPreset) => void
    onError?: (message: string) => void
    onRemove?: (presetId: string) => void
    onUpdate?: (presets: CopilotUploadPreset[]) => void
}

const CopilotApiContextManager = ({ children, onCreate, onError, onRemove, onUpdate }: Props) => {
    const { showMessage } = useContext(PortalMessageContext)
    const { user } = useContext(UserContext)
    const uploadContext = useContext(CreationToolUploadContext)

    function setIsLocked(preset: CopilotUploadPreset): CopilotUploadPreset {
        const is_locked = preset.author.username !== user.username

        return { ...preset, is_locked }
    }

    async function createNewPreset(title: string) {
        if (!title) return

        if (uploadContext.tags.length > MAX_TAG_LIMIT) {
            showMessage(`Presets have a limit of ${MAX_TAG_LIMIT} tags. Please remove some tags and try again.`, {
                style: 'error',
            })

            return
        }

        try {
            const data = createDataFromUploadContext(title, uploadContext)
            const created = await createCopilotPreset(data).then(setIsLocked)

            showMessage(<PresetCreatedMessage presetName={created.name} />)
            onCreate?.(created)
        } catch (err) {
            onError?.(`An error occurred while creating your preset.`)
        }
    }

    async function deletePreset(preset: CopilotUploadPreset) {
        try {
            await deleteCopilotPreset(preset.id)

            showMessage(<PresetDeletedMessage presetName={preset.name} />)
            onRemove?.(preset.id)
        } catch (err) {
            onError?.(`An error occurred while deleting preset.`)
        }
    }

    async function duplicatePreset(preset: CopilotUploadPreset, title?: string) {
        try {
            const name = title || `${preset.name} copy`
            const data = createDataFromPreset(name, preset)
            const duplicated = await createCopilotPreset(data).then(setIsLocked)

            showMessage(<PresetDuplicatedMessage presetName={preset.name} />)
            onUpdate?.([duplicated])
        } catch (err) {
            onError?.(`An error occurred while duplicating preset.`)
        }
    }

    async function importPresetsById(ids: string[], ignoreMessage: boolean = false) {
        try {
            const imported = await fetchCopilotPresetsById(ids).then((p) => p.map(setIsLocked))

            if (!ignoreMessage) {
                showMessage(<PresetImportedMessage presetName={imported[0].name} />)
            }
            onUpdate?.(imported)
        } catch (err) {
            onError?.(`An error occurred while importing preset.`)
        }
    }

    async function renamePreset(preset: CopilotUploadPreset, newName: string) {
        if (!newName || newName === preset.name) {
            return
        }

        try {
            const data = createDataFromPreset(newName, preset)
            const renamed = await updateCopilotPreset(preset.id, {
                ...data,
                update_datetime: preset.update_datetime,
            })

            showMessage(<PresetRenamedMessage oldPresetName={preset.name} presetName={newName} />)
            onUpdate?.([renamed])
        } catch (err) {
            onError?.('An error occurred while renaming preset.')
        }
    }

    async function updatePreset(preset: CopilotUploadPreset) {
        try {
            const data = createDataFromUploadContext(preset.name, uploadContext, preset.upload_optional_tags.tags)
            const updated = await updateCopilotPreset(preset.id, {
                ...data,
                update_datetime: preset.update_datetime,
            }).then(setIsLocked)

            showMessage(<PresetUpdatedMessage presetName={updated.name} />)
            onUpdate?.([updated])
        } catch (err) {
            onError?.(`An error occurred while updating preset.`)
        }
    }

    return (
        <CopilotApiContext.Provider
            value={{
                createNewPreset,
                deletePreset,
                duplicatePreset,
                importPresetsById,
                renamePreset,
                updatePreset,
            }}
        >
            {children}
        </CopilotApiContext.Provider>
    )
}

export default CopilotApiContextManager
